import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { invalidateEffectiveSkills, resetSkillCapabilitiesCache } from '../hooks/useEffectiveSkills';
import { resetConversationDetailCache } from '../hooks/useConversation';
import {
  authPathPrefix,
  clearAuthStorage,
  getAuthItem,
  removeAuthItem,
  setAuthItem,
} from '../utils/authStorage';

/**
 * Authentication state tracked by the context.
 */
interface AuthState {
  /** JWT access token, or null if not authenticated */
  token: string | null;
  /** Current user's ID, or null if not authenticated */
  userId: string | null;
  /** Current user's login name (username), or null if not authenticated */
  username: string | null;
  /** Current user's human-readable display name, or null if unavailable */
  displayName: string | null;
  /** Whether the user is currently authenticated */
  isAuthenticated: boolean;
  /** Whether the user has admin privileges */
  isAdmin: boolean;
}

/**
 * Authentication context value provided to consumers.
 */
interface AuthContextValue extends AuthState {
  /** Log in with username and password */
  login: (username: string, password: string) => Promise<void>;
  /** Log in directly with a JWT token (from OAuth callback) */
  loginWithToken: (token: string, userId: string, isAdmin?: boolean) => void;
  /** Log out the current user */
  logout: () => Promise<void>;
  /** Whether a login/logout operation is in progress */
  isLoading: boolean;
  /**
   * Whether the initial auth/admin resolution has completed. Starts false
   * when a saved token exists (because `isAdmin` is seeded to a safe `false`
   * until `getMe()` returns); flips true once `getMe()` resolves or rejects,
   * or immediately when there is no token. Route guards that gate on
   * `isAdmin` MUST wait for this before redirecting, otherwise a platform
   * admin landing on `/admin` via reload/deep-link is bounced to `/` during
   * the resolution window.
   */
  authResolved: boolean;
  /** Error message from the last failed auth operation */
  error: string | null;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

/**
 * Synchronously reads the persisted auth identity out of localStorage.
 *
 * Used as the LAZY INITIALIZER for `authState` so that the very FIRST render
 * of every consumer already observes `isAuthenticated: true` for a logged-in
 * user. Seeding a hardcoded logged-out literal instead made render #1 a
 * guaranteed false negative, and `App.tsx::ProtectedRoute` acted on it
 * irreversibly: React flushes child effects before parent effects, so the
 * `<Navigate to="/login">` emitted on that first render fired BEFORE the
 * hydration effect below could correct the state — a cold deep-link into any
 * protected page bounced to `/login` and then landed on `/`, losing the
 * intended destination. (Same window also made `AdminRoute`'s `authResolved`
 * gate unreachable, because its `!isAuthenticated` early-return ran first.)
 *
 * `isAdmin` is deliberately hardcoded `false`: admin status is resolved ONLY
 * by the server via `api.getMe()` below. localStorage is user-writable and
 * must never be trusted for elevation.
 */
const readStoredAuthState = (): AuthState => {
  const savedToken = getAuthItem('conductor_auth_token');
  if (!savedToken) {
    return {
      token: null,
      userId: null,
      username: null,
      displayName: null,
      isAuthenticated: false,
      isAdmin: false,
    };
  }
  return {
    token: savedToken,
    userId: getAuthItem('conductor_user_id'),
    username: getAuthItem('conductor_username'),
    displayName: getAuthItem('conductor_display_name'),
    isAuthenticated: true,
    // Never seeded from localStorage — see the docstring above.
    isAdmin: false,
  };
};

/**
 * Cookie `path` for the `conductor_auth` cookie: this build's API prefix
 * (`/conductor`, `/conductor_test_<slug>`, `/conductor-preview`; `/` on the
 * root-based cn build). Same source as `agentStream.ts::openAgentStream`'s SSE
 * base URL, so the cookie always reaches the stream endpoint.
 *
 * Why not `path=/`: every deployment on myconductor.net shares one origin, so a
 * site-wide cookie is overwritten by whichever deployment logged in last. A test
 * slot's token (own SECRET_KEY) then rides the production EventSource → 401,
 * which the frontend can only report as "network interrupted".
 * Alias of `utils/authStorage.ts::authPathPrefix`, which also partitions the
 * localStorage auth keys by the same prefix.
 */
export const authCookiePath = authPathPrefix;

const EXPIRED = 'expires=Thu, 01 Jan 1970 00:00:00 GMT';

/**
 * Expire the legacy site-wide `path=/` cookie written by pre-fix builds.
 * Called ONLY on logout / invalid-token cleanup — never on login or mount:
 * test slots that have not merged the fix yet still authenticate their SSE
 * with that `path=/` cookie, and the backend tolerates duplicate
 * `conductor_auth` cookies (`auth.py::get_current_user_or_cookie`).
 */
const clearLegacyRootAuthCookie = (path: string): void => {
  if (path !== '/') {
    document.cookie = `conductor_auth=; path=/; ${EXPIRED}`;
  }
};

const writeAuthCookie = (token: string): void => {
  const path = authCookiePath();
  document.cookie = `conductor_auth=${token}; path=${path}; SameSite=Strict; Secure`;
};

/**
 * Expire this deployment's cookie. `includeLegacy` (explicit logout only)
 * also expires the legacy `path=/` cookie; the invalid-token cleanup on mount
 * passes false, because during the rollout an un-updated test slot can
 * overwrite prod's localStorage token and the resulting 401 must not in turn
 * delete that slot's `path=/` SSE cookie (the M3 ping-pong).
 */
const clearAuthCookie = (includeLegacy = true): void => {
  const path = authCookiePath();
  if (includeLegacy) {
    clearLegacyRootAuthCookie(path);
  }
  document.cookie = `conductor_auth=; path=${path}; ${EXPIRED}`;
};

/**
 * AuthProvider wraps children with authentication state management.
 * The initial state is hydrated SYNCHRONOUSLY from localStorage via
 * `readStoredAuthState` (lazy initializer), and the mount effect below then
 * validates the token against the server.
 * Provides login/logout functions to children via context.
 */
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Pass the function REFERENCE (not a call) so localStorage is read exactly
  // once, at mount — `useState(readStoredAuthState())` would re-read on every
  // render and defeat lazy initialization.
  const [authState, setAuthState] = useState<AuthState>(readStoredAuthState);
  const [isLoading, setIsLoading] = useState(false);
  // False until the initial admin resolution finishes. Route guards that
  // depend on `isAdmin` must wait for this (see AuthContextValue.authResolved).
  const [authResolved, setAuthResolved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // On mount, check localStorage for an existing token and validate isAdmin from server
  useEffect(() => {
    const savedToken = getAuthItem('conductor_auth_token');
    const savedUserId = getAuthItem('conductor_user_id');
    const savedUsername = getAuthItem('conductor_username');
    const savedDisplayName = getAuthItem('conductor_display_name');
    if (savedToken) {
      // Set authenticated immediately with isAdmin=false (safe default)
      setAuthState({
        token: savedToken,
        userId: savedUserId,
        username: savedUsername,
        displayName: savedDisplayName,
        isAuthenticated: true,
        isAdmin: false,
      });
      writeAuthCookie(savedToken);

      // Validate isAdmin from server (prevents localStorage spoofing).
      // Phase 0 dual-key transition (P0-T5A): backend emits both
      // `is_platform_admin` (new canonical) and `is_admin` (legacy alias);
      // read via nullish-coalescing fallback. The localStorage key is also
      // renamed to `conductor_is_platform_admin` for consistency; legacy key
      // `conductor_is_admin` is cleared on this code path so stale values do
      // not linger. TODO P2: drop is_admin fallback after backend alias removal.
      api.getMe().then((me) => {
        const isPlatformAdmin = me.is_platform_admin ?? me.is_admin ?? false;
        const resolvedDisplayName = me.display_name || me.username;
        setAuthItem('conductor_is_platform_admin', String(isPlatformAdmin));
        removeAuthItem('conductor_is_admin');  // P2 cleanup: drop after sprint
        setAuthItem('conductor_username', me.username);
        setAuthItem('conductor_display_name', resolvedDisplayName);
        setAuthState((prev) => ({
          ...prev,
          isAdmin: isPlatformAdmin,
          username: me.username,
          displayName: resolvedDisplayName,
        }));
        setAuthResolved(true);
      }).catch(() => {
        // Token is invalid or expired — clear auth state
        clearAuthStorage();
        clearAuthCookie(false);
        setAuthState({
          token: null,
          userId: null,
          username: null,
          displayName: null,
          isAuthenticated: false,
          isAdmin: false,
        });
        setAuthResolved(true);
      });
    } else {
      // No saved token: nothing to resolve, so route guards can proceed
      // immediately (an unauthenticated user is redirected by isAuthenticated).
      setAuthResolved(true);
    }
  }, []);

  /**
   * Logs in with the given credentials.
   * Stores token and user ID in localStorage on success.
   */
  const login = useCallback(async (username: string, password: string): Promise<void> => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await api.login({ username, password });
      const token = response.access_token;
      const userId = response.user_id;
      // Phase 0 dual-key (P0-T5A): backend emits both fields; prefer
      // is_platform_admin, fall back to legacy is_admin during dual-emit sprint.
      // TODO P2: drop is_admin fallback after backend alias removal.
      const isAdmin = (response.is_platform_admin ?? response.is_admin) || false;

      setAuthItem('conductor_auth_token', token);
      setAuthItem('conductor_user_id', userId);
      setAuthItem('conductor_is_platform_admin', String(isAdmin));
      // Seed the username from the login form so the UI can show a name
      // immediately; the canonical display_name is backfilled by getMe below.
      setAuthItem('conductor_username', username);
      writeAuthCookie(token);

      setAuthState({
        token,
        userId,
        username,
        displayName: username,
        isAuthenticated: true,
        isAdmin,
      });
      // isAdmin is known synchronously from the login response.
      setAuthResolved(true);

      // Backfill the human-readable display_name (and canonical username)
      // without blocking the login resolve. Failure is non-fatal — the UI
      // already shows the form username.
      api.getMe().then((me) => {
        const resolvedDisplayName = me.display_name || me.username;
        setAuthItem('conductor_username', me.username);
        setAuthItem('conductor_display_name', resolvedDisplayName);
        setAuthState((prev) => ({
          ...prev,
          username: me.username,
          displayName: resolvedDisplayName,
        }));
      }).catch(() => { /* non-fatal: keep form username */ });
    } catch (err: unknown) {
      let message = 'Login failed. Please check your credentials.';
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosErr = err as { response?: { data?: { detail?: string } } };
        const detail = axiosErr.response?.data?.detail;
        if (detail) message = detail;
      }
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Logs out the current user.
   * Removes token and user ID from localStorage.
   */
  const logout = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    try {
      await api.logout();
    } catch {
      // Logout API failure is non-critical; clear local state anyway
    } finally {
      clearAuthStorage();
      // Clear active org selection on logout so the next user does not
      // inherit the previous user's org context. String literal (NOT an
      // import from OrgSwitcher) — see Plan Pack Contract Pack v1, the
      // canonical key is defined in OrgSwitcher.tsx::ACTIVE_ORG_STORAGE_KEY
      // (cross-org-security-followup A5, 2026-05-28).
      localStorage.removeItem('conductor.active_org_id');
      resetSkillCapabilitiesCache(); // per-user flags: never reuse across users
      invalidateEffectiveSkills(); // per-user skill lists (handoff trusts them optimistically)
      resetConversationDetailCache(); // tab-lifetime detail cache: never cross users
      clearAuthCookie();
      setAuthState({
        token: null,
        userId: null,
        username: null,
        displayName: null,
        isAuthenticated: false,
        isAdmin: false,
      });
      // Logged-out state is fully resolved.
      setAuthResolved(true);
      setIsLoading(false);
    }
  }, []);

  /**
   * Authenticates directly with a pre-obtained JWT token.
   * Used after WeCom OAuth callback redirect.
   */
  const loginWithToken = useCallback((token: string, userId: string, isAdmin?: boolean): void => {
    api.setAuth(token);
    setAuthItem('conductor_user_id', userId);
    // Phase 0 dual-key (P0-T5A): localStorage key renamed to is_platform_admin;
    // legacy key cleared on the validate path in the mount effect above.
    setAuthItem('conductor_is_platform_admin', String(isAdmin || false));
    writeAuthCookie(token);
    setAuthState({
      token,
      userId,
      username: null,
      displayName: null,
      isAuthenticated: true,
      isAdmin: isAdmin || false,
    });
    // isAdmin is provided by the OAuth callback caller; resolution done.
    setAuthResolved(true);

    // Backfill username + display_name (OAuth callback has no name payload).
    api.getMe().then((me) => {
      const resolvedDisplayName = me.display_name || me.username;
      setAuthItem('conductor_username', me.username);
      setAuthItem('conductor_display_name', resolvedDisplayName);
      setAuthState((prev) => ({
        ...prev,
        username: me.username,
        displayName: resolvedDisplayName,
      }));
    }).catch(() => { /* non-fatal */ });
  }, []);

  const value: AuthContextValue = {
    ...authState,
    login,
    loginWithToken,
    logout,
    isLoading,
    authResolved,
    error,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Custom hook to access the authentication context.
 * Must be used within an AuthProvider.
 */
export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
